<script setup lang="ts">
  import { computed } from "vue";
  import { useFormValues } from "vee-validate";
  import { AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
  import { FormItem, FormField, FormControl, FormLabel, FormMessage } from "@/components/ui/form";
  import { Input } from "@/components/ui/input";
  import { InputNumber } from "@/components/episto-ui/input-number";
  import { Select } from "@/components/episto-ui/select";
  import { DateTimePicker } from "@/components/episto-ui/date-time-picker";
  import ImageUploadField from "@/components/episto-ui/form-dialog/ImageUploadField.vue";
  import type { JcdReferentials } from "@/types";

  const props = defineProps<{
    referentials: JcdReferentials | null;
  }>();

  const values = useFormValues();

  const asOptions = (items: string[] | number[]) => items.map((item) => ({ label: String(item), value: item }));
  const imageAccept = computed(() => props.referentials?.image_mime_types.join(",") || "image/*");

  const yearOptions = computed(() => asOptions(Object.keys(props.referentials?.weeks_by_year || {}).map(Number)));
  const weekOptions = computed(() => {
    const weeksCount = props.referentials?.weeks_by_year[values.value.year as number] || 53;
    return Array.from({ length: weeksCount }, (_, i) => ({ label: `S${i + 1}`, value: i + 1 }));
  });
  const familyOptions = computed(() => asOptions(Object.keys(props.referentials?.families || {})));
  const familyClassOptions = computed(() => {
    const family = values.value.family as string | null;
    if (!family) return [];
    return asOptions(props.referentials?.families[family] || []);
  });
  const supportOptions = computed(() => asOptions(props.referentials?.supports || []));
  const networkOptions = computed(() => asOptions(props.referentials?.networks || []));
  const formatOptions = computed(() => asOptions(props.referentials?.formats || []));
  const levelOptions = computed(() => asOptions(props.referentials?.levels || []));
</script>

<template>
  <AccordionItem value="campaign">
    <AccordionTrigger class="text-base font-semibold">Campagne</AccordionTrigger>
    <AccordionContent class="space-y-4 px-1 pt-2">
      <FormField
        v-slot="{ componentField }"
        name="brand">
        <FormItem>
          <FormLabel>Marque <span class="text-destructive">*</span></FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Burger King"
              class="text-sm" />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField
        v-slot="{ value, handleChange }"
        name="logo_url">
        <FormItem>
          <FormControl>
            <ImageUploadField
              :model-value="value || ''"
              label="Logo"
              :accept="imageAccept"
              @update:model-value="handleChange" />
          </FormControl>
        </FormItem>
      </FormField>

      <div class="grid grid-cols-2 gap-4">
        <FormField
          v-slot="{ componentField }"
          name="year">
          <FormItem>
            <FormLabel>Année <span class="text-destructive">*</span></FormLabel>
            <FormControl>
              <Select
                v-bind="componentField"
                :options="yearOptions"
                placeholder="2026" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField
          v-slot="{ componentField }"
          name="week">
          <FormItem>
            <FormLabel>Semaine <span class="text-destructive">*</span></FormLabel>
            <FormControl>
              <Select
                v-bind="componentField"
                :options="weekOptions"
                filterable
                placeholder="S47" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField
          v-slot="{ componentField }"
          name="starts_on">
          <FormItem>
            <FormLabel>Date début <span class="text-destructive">*</span></FormLabel>
            <FormControl>
              <DateTimePicker
                v-bind="componentField"
                placeholder="Date de début" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField
          v-slot="{ componentField }"
          name="ends_on">
          <FormItem>
            <FormLabel>Date fin <span class="text-destructive">*</span></FormLabel>
            <FormControl>
              <DateTimePicker
                v-bind="componentField"
                placeholder="Date de fin" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField
          v-slot="{ componentField }"
          name="family">
          <FormItem>
            <FormLabel>Famille</FormLabel>
            <FormControl>
              <Select
                v-bind="componentField"
                :options="familyOptions"
                filterable
                clearable
                placeholder="Famille" />
            </FormControl>
          </FormItem>
        </FormField>

        <FormField
          v-slot="{ componentField }"
          name="family_class">
          <FormItem>
            <FormLabel>Classe</FormLabel>
            <FormControl>
              <Select
                v-bind="componentField"
                :options="familyClassOptions"
                :disabled="!values.family"
                filterable
                clearable
                placeholder="Classe" />
            </FormControl>
          </FormItem>
        </FormField>

        <FormField
          v-slot="{ componentField }"
          name="support">
          <FormItem>
            <FormLabel>Support</FormLabel>
            <FormControl>
              <Select
                v-bind="componentField"
                :options="supportOptions"
                clearable
                placeholder="Support" />
            </FormControl>
          </FormItem>
        </FormField>

        <FormField
          v-slot="{ componentField }"
          name="level">
          <FormItem>
            <FormLabel>Niveau campagne</FormLabel>
            <FormControl>
              <Select
                v-bind="componentField"
                :options="levelOptions"
                clearable
                placeholder="Niveau" />
            </FormControl>
          </FormItem>
        </FormField>

        <FormField
          v-slot="{ componentField }"
          name="gross_budget">
          <FormItem>
            <FormLabel>Budget brut (€)</FormLabel>
            <FormControl>
              <InputNumber
                v-bind="componentField"
                :min="0"
                placeholder="100 000" />
            </FormControl>
          </FormItem>
        </FormField>

        <FormField
          v-slot="{ componentField }"
          name="faces_count">
          <FormItem>
            <FormLabel>Nombre de faces</FormLabel>
            <FormControl>
              <InputNumber
                v-bind="componentField"
                :min="0"
                placeholder="250" />
            </FormControl>
          </FormItem>
        </FormField>

        <FormField
          v-slot="{ componentField }"
          name="visuals_count">
          <FormItem>
            <FormLabel>Nb visuels</FormLabel>
            <FormControl>
              <InputNumber
                v-bind="componentField"
                :min="0"
                placeholder="3" />
            </FormControl>
          </FormItem>
        </FormField>

        <FormField
          v-slot="{ componentField }"
          name="mosaic_code">
          <FormItem>
            <FormLabel>Code Mosaic</FormLabel>
            <FormControl>
              <Input
                v-bind="componentField"
                placeholder="Code Mosaic"
                class="text-sm" />
            </FormControl>
          </FormItem>
        </FormField>

        <FormField
          v-slot="{ componentField }"
          name="networks">
          <FormItem>
            <FormLabel>Réseau</FormLabel>
            <FormControl>
              <Select
                v-bind="componentField"
                :options="networkOptions"
                multiple
                clearable
                placeholder="Réseaux" />
            </FormControl>
          </FormItem>
        </FormField>

        <FormField
          v-slot="{ componentField }"
          name="formats">
          <FormItem>
            <FormLabel>Format</FormLabel>
            <FormControl>
              <Select
                v-bind="componentField"
                :options="formatOptions"
                multiple
                clearable
                placeholder="Formats" />
            </FormControl>
          </FormItem>
        </FormField>
      </div>

    </AccordionContent>
  </AccordionItem>
</template>
